<div class="cit-toolbar">
  <mat-form-field appearance="fill" class="cit-toolbar__search" subscriptSizing="dynamic">
    <mat-label>{{ 'classicImport.tree.search' | translate }}</mat-label>
    <input matInput data-testid="ci-tree-search" [value]="search()" (input)="onSearch($event)" />
  </mat-form-field>
  <mat-form-field appearance="fill" class="cit-toolbar__type" subscriptSizing="dynamic" data-testid="ci-tree-type">
    <mat-label>{{ 'classicImport.tree.typeFilter' | translate }}</mat-label>
    <mat-select [value]="type()" (valueChange)="type.set($event)">
      <mat-option [value]="null">{{ 'classicImport.tree.allTypes' | translate }}</mat-option>
      @for (t of types(); track t) {
        <mat-option [value]="t">{{ 'classicImport.tree.types.' + t | translate }}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  <button type="button" class="cit-link" data-testid="ci-tree-all" [disabled]="disabled()" (click)="selectAll()">
    {{ 'classicImport.tree.all' | translate }}
  </button>
  <button type="button" class="cit-link" data-testid="ci-tree-none" [disabled]="disabled()" (click)="selectNone()">
    {{ 'classicImport.tree.none' | translate }}
  </button>
</div>

@if (isEmpty()) {
  <p class="cit-empty" data-testid="ci-tree-empty">{{ 'classicImport.tree.noElements' | translate }}</p>
} @else if (rows().length === 0) {
  <p class="cit-empty" data-testid="ci-tree-empty">{{ 'classicImport.tree.empty' | translate }}</p>
} @else {
  <cdk-virtual-scroll-viewport
    class="cit-viewport"
    data-testid="ci-tree"
    [itemSize]="rowHeight"
    minBufferPx="216"
    maxBufferPx="432"
  >
    <div
      *cdkVirtualFor="let row of rows(); trackBy: trackRow"
      class="cit-row"
      [attr.data-testid]="'ci-tree-node-' + row.key"
      [attr.data-key]="row.key"
      [attr.data-kind]="row.kind"
      [class.cit-row--auto]="row.kind === 'item' && isAuto(row.key)"
      [style.padding-left.rem]="0.5 + row.depth * 1.25"
    >
      @if (row.kind === 'folder') {
        @let open = isExpanded(row.key);
        @let state = folderStateOf(row.key);
        <button
          type="button"
          class="cit-row__toggle"
          data-testid="ci-tree-toggle"
          [attr.aria-expanded]="open"
          [attr.aria-label]="
            (open ? 'classicImport.tree.collapse' : 'classicImport.tree.expand') | translate: { name: row.name }
          "
          (click)="toggleFolderOpen(row.key)"
        >
          <mat-icon aria-hidden="true">{{ open ? 'expand_more' : 'chevron_right' }}</mat-icon>
        </button>
        <mat-checkbox
          [checked]="state === 'all'"
          [indeterminate]="state === 'some'"
          [disabled]="disabled()"
          [aria-label]="row.name"
          (change)="onToggle(row, $event)"
        />
        <mat-icon class="cit-row__icon" aria-hidden="true">{{ open ? 'folder_open' : 'folder' }}</mat-icon>
        <span class="cit-name">{{ row.name }}</span>
      } @else {
        @let auto = isAuto(row.key);
        <span class="cit-row__spacer" aria-hidden="true"></span>
        <mat-checkbox
          [checked]="effective().has(row.key)"
          [disabled]="disabled() || auto"
          [aria-label]="row.name"
          (change)="onToggle(row, $event)"
        />
        <mat-icon class="cit-row__icon" aria-hidden="true">{{ icons[row.node.type] }}</mat-icon>
        <span class="sr-only">{{ 'classicImport.tree.types.' + row.node.type | translate }}</span>
        <span class="cit-name">{{ row.name }}</span>
        @if (auto) {
          @let hint = requiredByKey(row.key);
          <button
            type="button"
            class="cit-row__needed"
            data-testid="ci-tree-required-by"
            (click)="showNeededBy(row.key)"
          >
            {{ hint.key | translate: { n: hint.n } }}
          </button>
        }
      }
    </div>
  </cdk-virtual-scroll-viewport>
}

@if (neededBy(); as needed) {
  <div class="cit-needed-by" data-testid="ci-tree-needed-by" role="status">
    <span>
      {{ 'classicImport.tree.neededBy' | translate: { names: needed.names.join(', ') } }}
      @if (needed.more > 0) {
        {{ 'classicImport.tree.neededByMore' | translate: { n: needed.more } }}
      }
    </span>
    <button
      type="button"
      class="cit-needed-by__close"
      [attr.aria-label]="'classicImport.tree.closeNeededBy' | translate"
      (click)="closeNeededBy()"
    >
      <mat-icon aria-hidden="true">close</mat-icon>
    </button>
  </div>
}

<p class="cit-count" data-testid="ci-tree-count">
  {{
    (total() === 1 ? 'classicImport.tree.countOne' : 'classicImport.tree.count')
      | translate: { total: total(), auto: autoCount() }
  }}
</p>
